import { Box, Code, HStack, Text, VStack } from '@chakra-ui/react';
import type { ReactElement } from 'react';
import { ListIcon } from 'assets/icons/v2';
import { NovaIconButton } from 'components/ui/NovaIconButton';

/** German label for icon-only controls (styleguide QA). */
const ICON_BUTTON_ARIA_LABEL = 'Menü öffnen';

const RECIPE_SIZE_LABEL = {
  s: 's',
  m: 'm',
  l: 'l',
} as const;

/**
 * Matrix of UI-kit icon buttons for visual QA next to Figma (Secondary / Default, Primary / Standard,
 * Tertiary Circular). Excludes Circular Video / XL per kit spec.
 */
export const IconButtonPreview = (): ReactElement => {
  return (
    <VStack gap="xl" align="stretch" w="full">
      <VStack gap="xs" align="stretch">
        <Text textStyle="h3.medium" color="text.primary">
          Icon Button (UI-Kit)
        </Text>
        <Text textStyle="body.s.regular" color="text.tertiary">
          Vorschau für Rezept-Varianten <Code fontSize="sm">primary</Code> und{' '}
          <Code fontSize="sm">secondary</Code> (<Code fontSize="sm">s</Code> /{' '}
          <Code fontSize="sm">m</Code> / <Code fontSize="sm">l</Code>) sowie{' '}
          <Code fontSize="sm">tertiaryCircular</Code> über <Code fontSize="sm">NovaIconButton</Code>{' '}
          — Zustände normal, deaktiviert, Ladezustand.
        </Text>
      </VStack>

      <Box
        bg="background.canvas"
        p="xl"
        borderRadius="l"
        borderWidth="1px"
        borderColor="border.accent"
      >
        <VStack gap="2xl" align="stretch">
          <Text textStyle="h4.medium" color="text.primary">
            Icon Button
          </Text>

          <VStack gap="l" align="stretch">
            <Text textStyle="body.m.medium" color="text.secondary">
              secondary — Secondary / Default (S / M / L)
            </Text>
            {(['s', 'm', 'l'] as const).map((size) => (
              <VStack key={size} gap="xs" align="stretch">
                <HStack flexWrap="wrap" gap="m" align="center">
                  <NovaIconButton
                    type="button"
                    variant="secondary"
                    size={size}
                    aria-label={ICON_BUTTON_ARIA_LABEL}
                    icon={ListIcon}
                  />
                  <NovaIconButton
                    type="button"
                    variant="secondary"
                    size={size}
                    aria-label={ICON_BUTTON_ARIA_LABEL}
                    disabled
                    icon={ListIcon}
                  />
                  <NovaIconButton
                    type="button"
                    variant="secondary"
                    size={size}
                    aria-label={ICON_BUTTON_ARIA_LABEL}
                    loading
                    icon={ListIcon}
                  />
                </HStack>
                <Text textStyle="body.xs.regular" color="text.tertiary">
                  {`variant="secondary" size="${size}" → recipe variant="secondary" size="${RECIPE_SIZE_LABEL[size]}"`}
                </Text>
              </VStack>
            ))}
          </VStack>

          <VStack gap="l" align="stretch">
            <Text textStyle="body.m.medium" color="text.secondary">
              primary — Primary / Standard (S / M / L)
            </Text>
            {(['s', 'm', 'l'] as const).map((size) => (
              <VStack key={size} gap="xs" align="stretch">
                <HStack flexWrap="wrap" gap="m" align="center">
                  <NovaIconButton
                    type="button"
                    variant="primary"
                    size={size}
                    aria-label={ICON_BUTTON_ARIA_LABEL}
                    icon={ListIcon}
                  />
                  <NovaIconButton
                    type="button"
                    variant="primary"
                    size={size}
                    aria-label={ICON_BUTTON_ARIA_LABEL}
                    disabled
                    icon={ListIcon}
                  />
                  <NovaIconButton
                    type="button"
                    variant="primary"
                    size={size}
                    aria-label={ICON_BUTTON_ARIA_LABEL}
                    loading
                    icon={ListIcon}
                  />
                </HStack>
                <Text textStyle="body.xs.regular" color="text.tertiary">
                  {`variant="primary" size="${size}" → recipe variant="primary" size="${RECIPE_SIZE_LABEL[size]}"`}
                </Text>
              </VStack>
            ))}
          </VStack>

          <VStack gap="l" align="stretch">
            <Text textStyle="body.m.medium" color="text.secondary">
              tertiaryCircular — Tertiary / Circular (nur M in Figma)
            </Text>
            <VStack gap="xs" align="stretch">
              <HStack flexWrap="wrap" gap="m" align="center">
                <NovaIconButton
                  type="button"
                  variant="tertiaryCircular"
                  aria-label={ICON_BUTTON_ARIA_LABEL}
                  icon={ListIcon}
                />
                <NovaIconButton
                  type="button"
                  variant="tertiaryCircular"
                  aria-label={ICON_BUTTON_ARIA_LABEL}
                  disabled
                  icon={ListIcon}
                />
                <NovaIconButton
                  type="button"
                  variant="tertiaryCircular"
                  aria-label={ICON_BUTTON_ARIA_LABEL}
                  loading
                  icon={ListIcon}
                />
              </HStack>
              <Text textStyle="body.xs.regular" color="text.tertiary">
                variant=&quot;tertiaryCircular&quot; (ohne <Code fontSize="sm">size</Code>) → recipe
                variant=&quot;tertiaryCircular&quot; size=&quot;m&quot; mit Icon-Größe &quot;l&quot;
              </Text>
            </VStack>
          </VStack>
        </VStack>
      </Box>
    </VStack>
  );
};
